import React, { useState, useEffect } from 'react';
import { AlertCircle, Clock, ChevronRight, CheckCircle2, Loader2 } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TaskSummaryMessage = ({ message, onScrollToTask }) => {
  const { isDarkMode } = useTheme();
  const { token } = useAuth();
  const [tasks, setTasks] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(false);

  // Use task_items from message if available, otherwise fetch from API
  useEffect(() => {
    if (message.task_items && message.task_items.length > 0) {
      setTasks({
        overdue: message.task_items.filter(t => t.category === 'overdue'),
        due_today: message.task_items.filter(t => t.category === 'due_today'),
        upcoming: message.task_items.filter(t => t.category === 'upcoming'),
      });
    }
  }, [message.task_items]);

  const fetchTasks = async () => {
    if (tasks) {
      setExpanded(!expanded);
      return;
    }
    setLoading(true);
    try {
      const res = await axios.get(`${API}/tasks/pending-summary`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTasks(res.data);
      setExpanded(true);
    } catch (err) {
      console.error('Failed to fetch tasks:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleTaskClick = (taskId) => {
    if (onScrollToTask) {
      onScrollToTask(taskId);
    }
  };

  const totalCount = tasks
    ? (tasks.overdue?.length || 0) + (tasks.due_today?.length || 0) + (tasks.upcoming?.length || 0)
    : 0;

  const renderSection = (title, icon, color, items) => {
    if (!items || items.length === 0) return null;
    return (
      <div className="mt-2">
        <div className={`flex items-center gap-1.5 text-xs font-semibold ${color} mb-1`}>
          {icon}
          {title} ({items.length})
        </div>
        <div className="space-y-1">
          {items.map((task, idx) => (
            <button
              key={task.id || idx}
              onClick={() => handleTaskClick(task.id)}
              data-testid={`task-summary-item-${task.id || idx}`}
              className={`w-full text-left flex items-center gap-2 px-2 py-1.5 rounded-lg transition-colors ${
                isDarkMode
                  ? 'hover:bg-gray-600/50 text-gray-200'
                  : 'hover:bg-gray-100 text-gray-700'
              }`}
            >
              <span className={`text-xs flex-shrink-0 w-4 text-right ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>{idx + 1}.</span>
              <span className="text-sm truncate flex-1">{task.title}</span>
              <ChevronRight size={14} className={isDarkMode ? 'text-gray-500' : 'text-gray-400'} />
            </button>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div
      className={`rounded-2xl overflow-hidden max-w-md mx-auto ${
        isDarkMode ? 'bg-gray-800 border border-gray-700' : 'bg-white border border-gray-200 shadow-sm'
      }`}
      data-testid="task-summary-message"
    >
      {/* Header */}
      <button
        onClick={() => tasks ? setExpanded(!expanded) : fetchTasks()}
        className={`w-full flex items-center justify-between px-4 py-3 transition-colors ${
          isDarkMode ? 'hover:bg-gray-700/50' : 'hover:bg-gray-50'
        }`}
        data-testid="task-summary-toggle"
      >
        <div className="flex items-center gap-2">
          <span className="text-lg">📋</span>
          <span className={`font-semibold text-sm ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
            Task Summary
          </span>
          {totalCount > 0 && (
            <span className={`text-xs px-2 py-0.5 rounded-full ${
              isDarkMode ? 'bg-[#D4AF37]/20 text-[#D4AF37]' : 'bg-[#D4AF37]/10 text-[#B8941F]'
            }`}>
              {totalCount} pending
            </span>
          )}
        </div>
        {loading ? (
          <Loader2 size={16} className="animate-spin text-[#D4AF37]" />
        ) : (
          <ChevronRight size={16} className={`transition-transform ${expanded ? 'rotate-90' : ''} ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`} />
        )}
      </button>

      {/* Expanded task list */}
      {expanded && tasks && (
        <div className={`px-4 pb-3 border-t ${isDarkMode ? 'border-gray-700' : 'border-gray-100'}`}>
          {renderSection(
            'OVERDUE', 
            <AlertCircle size={12} />, 
            'text-red-500', 
            tasks.overdue
          )}
          {renderSection(
            'DUE TODAY', 
            <Clock size={12} />, 
            'text-yellow-500', 
            tasks.due_today
          )}
          {renderSection(
            'UPCOMING', 
            <CheckCircle2 size={12} />, 
            'text-green-500', 
            tasks.upcoming
          )}
          {totalCount === 0 && (
            <p className={`text-sm text-center py-3 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
              No pending tasks
            </p>
          )}
        </div>
      )}
    </div>
  );
};

export default TaskSummaryMessage;
